import {
  type CreateTemplateRequest,
  TemplateStatus,
  TemplateType,
} from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { useCreateTemplateMutation } from '../../graphql/hooks/useCreateTemplateMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { TemplateForm } from './TemplateForm';

type FormValues = CreateTemplateRequest;

const schema: z.ZodType<FormValues> = z.object({
  type: z.enum(TemplateType),
  name: z.string(),
  labelIds: z.array(z.string()),
  status: z.enum(TemplateStatus),
});

export const TemplateCreateModal = createModal((props) => {
  const { modal } = props;
  const { errorToast, successToast, organization } = useAppContext();

  const { hide, remove, visible } = modal;

  const methods = useForm<FormValues>({
    defaultValues: {
      name: '',
      type: TemplateType.ParticipantDocument,
      labelIds: [],
      status: TemplateStatus.Draft,
    },
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const mutation = useCreateTemplateMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          request: values,
        })
        .then(() => {
          hide();
          successToast('Template created successfully');
        })
        .catch((err) => {
          errorToast(`Error creating template: ${err.message}`);
        });
    },
    [mutation, organization.id, hide, successToast, errorToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Create a template'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <TemplateForm />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Create</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
